import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { client, urlFor } from '../client'; // Import Sanity client & urlFor
import { PortableText } from '@portabletext/react'; // Import PortableText

// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return '';

  // If dateString is YYYY-MM-DD, replace hyphens with slashes to parse as local date.
  let processedDateString = dateString;
  if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    processedDateString = dateString.replace(/-/g, '/');
  }

  const date = new Date(processedDateString);
  const options = { year: 'numeric', month: 'long', day: 'numeric' };
  return date.toLocaleDateString('en-US', options);
};

// Reusable PortableText components (consider moving to a separate file if used elsewhere)
const portableTextComponents = {
  types: {
    image: ({ value }) => {
      if (!value?.asset?._ref) {
        return null;
      }
      return (
        <img
          alt={value.alt || ' '}
          loading="lazy"
          src={urlFor(value).width(800).fit('max').auto('format').url()}
          className="my-6 rounded shadow-md" // Add some styling
        />
      );
    }
  },
  marks: {
    link: ({children, value}) => {
      const rel = !value.href.startsWith('/') ? 'noopener noreferrer' : undefined
      const target = !value.href.startsWith('/') ? '_blank' : undefined
      return (
        <a href={value.href} rel={rel} target={target} className="text-[#691e31] hover:underline">
          {children}
        </a>
      )
    },
  },
  block: {
    h2: ({children}) => <h2 className="text-2xl font-bold mt-8 mb-4 text-[#2a4865]">{children}</h2>,
    h3: ({children}) => <h3 className="text-xl font-bold mt-6 mb-3 text-[#2a4865]">{children}</h3>,
    h4: ({children}) => <h4 className="text-lg font-bold mt-4 mb-2 text-[#2a4865]">{children}</h4>,
    blockquote: ({children}) => <blockquote className="border-l-4 border-[#691e31] pl-4 py-2 my-6 italic text-gray-700">{children}</blockquote>,
    normal: ({children}) => <p className="text-gray-700 mb-6 leading-relaxed">{children}</p>,
  },
  list: {
    bullet: ({children}) => <ul className="list-disc pl-5 mb-6 space-y-2 text-gray-700">{children}</ul>,
    number: ({children}) => <ol className="list-decimal pl-5 mb-6 space-y-2 text-gray-700">{children}</ol>,
  },
  listItem: {
    bullet: ({children}) => <li>{children}</li>,
    number: ({children}) => <li>{children}</li>,
  },
};

// Placeholder for loading state (same as before)
const LoadingSpinner = () => (
  <div className="flex justify-center items-center min-h-[50vh]">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#691e31]"></div>
  </div>
);

// Placeholder for error state (same as before)
const NotFoundMessage = () => (
  <div className="container mx-auto px-4 py-16 text-center">
    <h2 className="text-2xl font-bold text-gray-700 mb-4">News Article Not Found</h2>
    <p className="text-gray-600 mb-8">The news article you're looking for doesn't exist or has been removed.</p>
    <Link 
      to="/" 
      className="inline-flex items-center bg-[#691e31] text-white hover:bg-[#7f2a3e] font-semibold px-5 py-2 rounded transition-colors"
    >
      Return to Home
    </Link>
  </div>
);

const NewsDetailPage = () => {
  const { slug } = useParams(); // Get slug from URL parameter
  const [articleData, setArticleData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null); // Add error state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedMediaItem, setSelectedMediaItem] = useState(null);

  const openLightbox = (item) => {
    setSelectedMediaItem(item);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setSelectedMediaItem(null);
  };

  useEffect(() => {
    if (!slug) {
      // console.log("NewsDetailPage: No slug found.");
      setLoading(false); // Stop loading if no slug
      setError('No article specified.');
      return;
    }

    // console.log(`NewsDetailPage: Fetching article with slug: ${slug}`);

    // Fetch the specific news article using the slug
    const query = `*[_type == "newsArticle" && slug.current == $slug][0]{
      title,
      publicationDate,
      mainImage { asset->{ _id, url } },
      body,
      mediaGallery[]{
        _key,
        title,
        mediaType,
        imageFile{asset->{_id, url, mimeType}, hotspot, crop, altText},
        videoUrl,
        videoUpload{asset->{_id, url, mimeType, originalFilename}},
        altText
      }
    }`;
    const params = { slug };

    setLoading(true);
    setError(null);
    client.fetch(query, params)
      .then((data) => {
        // console.log("NewsDetailPage: Fetched data:", data);
        if (data) {
          setArticleData(data);
          setError(null); // Clear previous errors if data found
        } else {
          // Set error if no data is found for the slug
          setError('Article not found.');
          setArticleData(null); // Clear any old data
        }
        setLoading(false);
        // console.log("NewsDetailPage: Loading set to false (in .then)");
      })
      .catch(err => {
        // console.error('Error fetching news article:', err);
        setError('Failed to load article content.');
        setArticleData(null); // Clear any old data
        setLoading(false);
        // console.log("NewsDetailPage: Loading set to false (in .catch)");
      });

  }, [slug]); // Re-run effect if slug changes

  useEffect(() => {
    // Close lightbox if escape key is pressed
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && lightboxOpen) {
        closeLightbox();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [lightboxOpen]);

  if (loading) {
    return <LoadingSpinner />;
  }

  // Use the error state or check if articleData is null
  if (error || !articleData) {
    return <NotFoundMessage />;
  }

  return (
    <div className="bg-white">
      {/* News Article Banner */}
      <div className="relative">
        <div className="h-64 md:h-80 overflow-hidden relative">
          {articleData.mainImage?.asset?.url ? (
            <img 
              src={urlFor(articleData.mainImage).width(1200).url()} // Use Sanity image URL
              alt={articleData.title} 
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gray-300"></div> // Placeholder if no image
          )}
          <div className="absolute inset-0 bg-black/40 flex items-center">
            <div className="container mx-auto px-4">
              <h1 className="text-white text-3xl md:text-4xl font-bold mb-4">{articleData.title}</h1>
              <div className="flex text-white/90">
                <Link to="/" className="hover:text-yellow-400 transition-colors">Home</Link>
                <span className="mx-2">/</span>
                <span className="truncate max-w-xs">{articleData.title}</span> {/* Use dynamic title */}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* News Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <div className="flex items-center text-sm text-gray-500 mb-6">
              {/* Format publication date */}
              <span>{formatDate(articleData.publicationDate)}</span>
              {/* Author info could be added to schema if needed */}
              {/* <span className="mx-2">•</span> */}
              {/* <span>By PS52 Administration</span> */}
            </div>
            
            {/* Render Portable Text body */}
            <div className="prose max-w-none">
                {articleData.body && (
                   <PortableText 
                      value={articleData.body} 
                      components={portableTextComponents} 
                   />
                )}

              {/* Media Gallery Section - UI Placeholder - Now Dynamic */}
              {(() => {
                const mediaItems = articleData?.mediaGallery || [];

                if (mediaItems.length === 0) {
                  return null; 
                }

                return (
                  <div className="mt-12 pt-8 border-t border-gray-200">
                    <h3 className="text-2xl font-bold text-[#2a4865] mb-6">Media Gallery</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {mediaItems.map(item => (
                        <div 
                          key={item._key} 
                          className="aspect-w-1 aspect-h-1 bg-gray-200 rounded-lg flex items-center justify-center group overflow-hidden relative cursor-pointer"
                          onClick={() => openLightbox(item)} // Open lightbox on click
                        >
                          {item.mediaType === 'image' && item.imageFile?.asset?.url ? (
                            <img 
                              src={urlFor(item.imageFile).width(400).height(400).fit('crop').auto('format').url()}
                              alt={item.altText || item.title || 'Gallery image'}
                              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                              loading="lazy"
                            />
                          ) : (item.mediaType === 'videoUrl' || item.mediaType === 'videoFile') ? (
                            <div className="w-full h-full flex items-center justify-center bg-black">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-white opacity-80" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            </div>
                          ) : (
                            // Fallback/Error Icon if image is missing for image type
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          )}
                          <div 
                            className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center p-2 text-center"
                          >
                            {item.title && <p className="text-white text-sm font-semibold mb-1 drop-shadow-md">{item.title}</p>}
                            <p className="text-yellow-400 text-xs font-bold drop-shadow-md">
                              {item.mediaType === 'image' ? 'View Image' : (item.mediaType === 'videoFile' ? 'Play Video File' : 'Play Video URL')}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
              
              {/* Static Return to Home button */}
              <div className="flex justify-center mt-12 pt-8 border-t border-gray-200">
                <Link 
                  to="/" 
                  className="inline-flex items-center bg-[#691e31] text-white hover:bg-[#7f2a3e] font-semibold px-6 py-3 rounded transition-colors group"
                >
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    className="h-4 w-4 mr-2" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  Return to Home
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox Modal */}
      {lightboxOpen && selectedMediaItem && (
        <div 
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100] p-4 backdrop-blur-sm"
          onClick={closeLightbox} // Close on overlay click
        >
          <div 
            className="bg-white p-3 rounded-lg shadow-2xl relative max-w-[90vw] max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()} // Prevent overlay click from closing if content is clicked
          >
            <button 
              onClick={closeLightbox} 
              className="absolute -top-3 -right-3 text-white bg-gray-800 hover:bg-red-600 rounded-full w-8 h-8 flex items-center justify-center text-xl z-10"
              aria-label="Close lightbox"
            >
              &times;
            </button>
            
            {selectedMediaItem.mediaType === 'image' && selectedMediaItem.imageFile?.asset?.url && (
              <img 
                src={urlFor(selectedMediaItem.imageFile).maxWidth(1600).maxHeight(1200).fit('max').auto('format').url()} 
                alt={selectedMediaItem.altText || selectedMediaItem.title || 'Enlarged gallery image'} 
                className="max-w-full max-h-[80vh] object-contain rounded"
              />
            )}
            {selectedMediaItem.mediaType === 'videoFile' && selectedMediaItem.videoUpload?.asset?.url && (
              <video 
                controls 
                autoPlay
                src={selectedMediaItem.videoUpload.asset.url} 
                className="max-w-full max-h-[80vh] rounded outline-none"
              >
                Your browser does not support the video tag.
              </video>
            )}
            {selectedMediaItem.mediaType === 'videoUrl' && selectedMediaItem.videoUrl && (
              <div className="p-4 text-center">
                <p className="text-lg text-gray-700 mb-4">This video is hosted externally. Click to view:</p>
                <a 
                  href={selectedMediaItem.videoUrl} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="inline-block bg-[#691e31] text-white hover:bg-[#7f2a3e] font-semibold px-5 py-2 rounded transition-colors"
                >
                  Open Video: {selectedMediaItem.title || 'Link'}
                </a>
              </div>
            )}
            {selectedMediaItem.title && (
                <p className="text-center text-sm text-gray-700 mt-3 bg-gray-100 p-2 rounded-b-lg">
                    {selectedMediaItem.title}
                </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsDetailPage; 